import { useCallback } from 'react';
import { cloneDeep, extend, get, omit, some } from 'lodash';
import { syncChildTestAgents } from '../helpers/WifiMultiTestHelper';
import { getSystemTime } from '../../../../../stores/SystemTimeStore';
import { prepareScheduleForSave, SCHEDULE_FLAT_FIELDS } from '../../ServiceTestsScheduleTransforms';
import { DEFAULT_INTERVAL_SECONDS, INTERVAL, INTERVAL_TYPE_FORMAT } from '../../ServiceTestsScheduleConstants';
import { TYPE_VOIP, WIFI_TEST_TYPES, WIRELESS } from '../../ServiceTestsConstants';
import { DEFAULT_JITTER } from '../../../../panels/voip-test-now-panels/VoipTestConstants';
import { AUTOMATIC, GLOBAL, MANUAL } from './UseTestThresholdsState';
import { MULTI } from '../../../general/Constants';
import { AUTH_NONE } from '../../../../panels/web-test-now-panels/WebTestConstants';
import { isBttTestType } from '../TestHelper';

const TEST_NEW_DETAIL_OBJECT = {};

/**
 * Compute a name that fits within maxLength.
 * If the combined test name + metric title exceeds maxLength,
 * the test name portion is trimmed (preserving at least 15 chars).
 * Mirrors portal/ui's trimDownName().
 */
function trimDownName(testName, metricTitle, appNameLength, maxLength) {
    if (appNameLength <= maxLength) {
        return `${testName}-${metricTitle}`;
    }
    const trimLimit = 15;
    const trimmedTestName = testName.slice(0, maxLength - (Math.min(appNameLength, trimLimit) + 1));
    return `${trimmedTestName}-${metricTitle}`;
}

/**
 * Hook that encapsulates all 7 save transforms, the compose chain (submitTransform),
 * and the createSubmitTransform factory. Extracted from TestDetailPanel.jsx for
 * separation of concerns (Phase 8 — SPLIT-01, READ-02).
 *
 * @param {object} params
 * @param {object} params.testType - Current test type object
 * @param {string} params.typeId - Test type _id
 * @param {object} params.dataService - API data service instance
 * @param {React.RefObject} params.bttStateRef - Ref to BTT certificate UI state
 * @param {React.RefObject} params.wifiTestListRef - Ref to WiFi child test list
 * @param {React.RefObject} params.matchingAgentsRef - Ref to matching agents array
 * @returns {{ submitTransform: Function, createSubmitTransform: Function }}
 */
export function useSubmitTransforms({
    testType,
    typeId,
    dataService,
    bttStateRef,
    wifiTestListRef,
    matchingAgentsRef,
    thresholdsRef,
    authTypeRef,
}) {
    // Applied on BOTH create and update — only converts flat schedule fields to the
    // nested scheduling object the API expects. Safe to run on updates because it
    // does not touch type, createdAt, or updatedAt.
    const scheduleTransform = useCallback((dataToSave) => {
        // Guard: if flat schedule fields are absent (ScheduleFields never mounted),
        // preserve whatever scheduling is already in the payload rather than
        // silently emitting a broken interval default.
        if (!('scheduleType' in dataToSave)) {
            // For Wi-Fi Group Test children (adapterType='multi'), ScheduleFields unmounts
            // and shouldUnregister loses flat fields. Ensure scheduling has defaults for the
            // API — portal/ui's prepareScheduleForScreen always sets interval=600 during init.
            // The child's schedule is inherited from the parent at runtime; this is a placeholder.
            if (dataToSave.adapterType === MULTI) {
                const scheduling = dataToSave.scheduling || {};
                if (scheduling.interval == null || scheduling.interval === '') {
                    return {
                        ...dataToSave,
                        scheduling: {
                            ...scheduling,
                            type: INTERVAL,
                            typeFormat: INTERVAL_TYPE_FORMAT,
                            scheduleType: INTERVAL,
                            interval: DEFAULT_INTERVAL_SECONDS,
                            isWindow: false,
                        },
                    };
                }
            }
            return dataToSave;
        }

        const { scheduling: schedulingPayload } = prepareScheduleForSave(dataToSave);
        return { ...omit(dataToSave, SCHEDULE_FLAT_FIELDS), scheduling: schedulingPayload };
    }, []);

    // Applied on BOTH create and update — strips React-only tagOp field, ensures
    // all backend-required agent fields are present, and enforces mode invariants.
    // Defense-in-depth: even if RHF field lifecycle loses a setValue() change (e.g. Controller
    // unmount race), the invariants below guarantee mutually exclusive agent selection data.
    const agentsTransform = useCallback((dataToSave) => {
        if (!dataToSave.agents) {
            return dataToSave;
        }
        const cleanAgents = { ...dataToSave.agents };

        if (cleanAgents.matchAllTags === undefined) {
            cleanAgents.matchAllTags = false;
        }

        if (cleanAgents.all === undefined) {
            cleanAgents.all = false;
        }

        if (!Array.isArray(cleanAgents.ids)) {
            cleanAgents.ids = [];
        }

        if (!Array.isArray(cleanAgents.tags)) {
            cleanAgents.tags = [];
        }

        // Mode invariants: all/selected/tagged are mutually exclusive.
        // When all=true, ids and tags must be empty (NGP-4010 defense-in-depth).
        if (cleanAgents.all === true) {
            cleanAgents.ids = [];
            cleanAgents.tags = [];
            cleanAgents.matchAllTags = false;
        }

        // When tags are selected (tagged mode), ids must be empty.
        if (cleanAgents.tags.length > 0) {
            cleanAgents.ids = [];
        }

        return { ...dataToSave, agents: cleanAgents };
    }, []);

    // Maps the virtual beaconMode field back to API fields on save.
    // Computes beaconPackets, hasBeaconSteps, and nG1 names for steps.
    const beaconTransform = useCallback((dataToSave) => {
        const { beaconMode } = dataToSave;

        // If beaconMode was never set (section not rendered), pass through
        if (!beaconMode) {
            return dataToSave;
        }

        const isEnabled = beaconMode !== 'disable';
        const isStepMode = beaconMode === 'step';

        // Set beaconPackets based on mode
        const beaconPackets = isEnabled;

        // Process beaconSteps
        let beaconSteps = Array.isArray(dataToSave.beaconSteps) ? dataToSave.beaconSteps : [];
        if (!isStepMode) {
            beaconSteps = beaconSteps.map((step) => ({ ...step, enabled: false }));
        }

        // Compute hasBeaconSteps (true only if step mode and at least one enabled)
        const hasBeaconSteps = isStepMode && some(beaconSteps, { enabled: true });

        // Compute nG1 names for steps if there are any
        if (beaconSteps.length > 0) {
            const testName = dataToSave.name || '';
            const nG1AppNameMaxLength = 100;
            const nG1ShortAppNameMaxLength = 29;
            const shortNamePrefix = 'nP-';

            // Find the longest step title to determine if trimming is needed
            const maxStepTitleLength = Math.max(...beaconSteps.map((s) => (s.metricTitle || '').length));
            const appNameLength = testName.length + maxStepTitleLength + 1;

            beaconSteps = beaconSteps.map((step) => {
                const enabled = step.enabled || false;
                const metricTitle = step.metricTitle || step.metricName || '';
                const nameForNg1 = trimDownName(testName, metricTitle, appNameLength, nG1AppNameMaxLength);
                const shortNameForNg1 = `${shortNamePrefix}${trimDownName(
                    testName,
                    metricTitle,
                    appNameLength,
                    nG1ShortAppNameMaxLength
                )}`;
                return { ...step, nameForNg1, shortNameForNg1, enabled };
            });
        }

        // Strip the virtual beaconMode field and set API fields
        const { beaconMode: _removed, ...rest } = dataToSave;
        return { ...rest, beaconPackets, beaconSteps, hasBeaconSteps };
    }, []);

    // Applies thresholds state from virtual RHF fields and converts numeric fields.
    // The virtual fields (thresholdsType, thresholdsSensitivity) are registered by
    // UseTestThresholdsState and included in dataToSave by handleSubmit().
    const thresholdsTransform = useCallback(
        (dataToSave) => {
            // Strip virtual fields (_thresholdsDirty sentinel + thresholdsType radio)
            const { thresholdsType, _thresholdsDirty, ...result } = dataToSave;

            // Inject current threshold values from ref (bypasses RHF unregistered field limitation)
            if (Array.isArray(thresholdsRef?.current) && thresholdsRef.current.length > 0) {
                result.thresholds = thresholdsRef.current;
            }

            if (!result.thresholds?.length) {
                return result;
            }

            const type = thresholdsType || GLOBAL;

            if (type === MANUAL) {
                result.overrideThreshold = true;
                result.dynamicThresholds = false;
            } else if (type === AUTOMATIC) {
                result.overrideThreshold = true;
                result.dynamicThresholds = true;
                // thresholdsSensitivity is already in result from dataToSave
            } else {
                // Global: restore testType thresholds and global settings
                result.overrideThreshold = false;
                result.dynamicThresholds = !!testType?.dynamicThresholds;
                result.thresholdsSensitivity = testType?.thresholdsSensitivity;
                result.thresholds = cloneDeep(testType?.thresholds || []);
            }

            const toNumber = (val) => (val !== '' && val != null ? parseFloat(val) : val);
            result.thresholds = (result.thresholds || []).map((threshold) => ({
                ...threshold,
                degraded: toNumber(threshold.degraded),
                critical: toNumber(threshold.critical),
                // portal/ui thresholds.service adds enableExcessive/enableDegraded (default true)
                enableExcessive: threshold.enableExcessive !== false,
                enableDegraded: threshold.enableDegraded !== false,
            }));

            return result;
        },
        [testType, thresholdsRef]
    );

    // Filters empty postData entries, ensures testConfig defaults, and restores hidden field defaults.
    const testConfigTransform = useCallback(
        (dataToSave) => {
            let { testConfig } = dataToSave;
            if (!testConfig) {
                return dataToSave;
            }

            // portal/ui: assignDefaultFields sets disableServerCertVerification=false for ALL test types.
            // Only apply when missing (not yet set by a UI toggle like TestRequestParametersSection).
            if (testConfig.disableServerCertVerification === undefined) {
                testConfig = { ...testConfig, disableServerCertVerification: false };
            }

            if (testConfig.postData) {
                const filteredPostData = testConfig.postData.filter((entry) => entry.name || entry.value);
                testConfig = { ...testConfig, postData: filteredPostData };
            }

            // portal/ui clearHiddenFields (testsDetails.controller.js:1103-1123):
            // Fields with showField/showValue are conditionally visible in DynamicFieldRenderer.
            // When hidden, DynamicFieldRenderer returns null and shouldUnregister:true drops
            // the field from RHF state. Reset hidden fields to their defaults so the payload
            // always includes required-but-hidden fields (e.g. port=80 when protocol=ICMP).
            const sections = testType?.testBuilder?.details?.sections;
            if (sections) {
                sections.forEach((section) => {
                    (section.fields || []).forEach((row) => {
                        (row || []).filter(Boolean).forEach((field) => {
                            if (!field.showField) {
                                return;
                            }
                            const actualValue = get(testConfig, field.showField);
                            if (String(actualValue) !== String(field.showValue)) {
                                testConfig = { ...testConfig, [field.title]: field.default ?? null };
                            }
                        });
                    });
                });
            }

            // Clean up authentication payload: keep only the user-selected auth type.
            // Problem: validateTest() runs deepMergeForSave() before every submit, which
            // merges the old formData auth (e.g. basic from server) with the new submitData
            // auth (e.g. ntlm from registered Controllers), producing both types in the payload.
            //
            // Fix: use authTypeRef.current (kept in sync by TestAuthenticationSection) to
            // identify exactly which auth type the user selected, then discard all others.
            // Mirrors portal/ui's webTestConfig.directive.js:onChangeAuth() _.pick() logic.
            if (testConfig.auth !== undefined) {
                const selectedAuthType = authTypeRef?.current;

                if (!selectedAuthType || selectedAuthType === AUTH_NONE) {
                    // User selected 'none' — clear auth entirely
                    testConfig = { ...testConfig, auth: {} };
                } else if (testConfig.auth && testConfig.auth[selectedAuthType]) {
                    // Keep only the user-selected auth type, drop all others
                    testConfig = {
                        ...testConfig,
                        auth: { [selectedAuthType]: testConfig.auth[selectedAuthType] },
                    };
                } else {
                    // Selected type not present in merged data (edge case) — clear auth
                    testConfig = { ...testConfig, auth: {} };
                }
            }

            return testConfig !== dataToSave.testConfig ? { ...dataToSave, testConfig } : dataToSave;
        },
        [authTypeRef, testType?.testBuilder?.details?.sections]
    );

    // Cleans BTT authentication and bttUser fields based on toggle states.
    const bttTransform = useCallback(
        (dataToSave) => {
            const { certAuthEnabled, runAsUser, defaultLogin } = bttStateRef.current;
            const isBtt = isBttTestType(testType?.bttTest, testType?.showBttAuth);
            if (!isBtt) {
                return dataToSave;
            }

            const result = { ...dataToSave };

            if (!certAuthEnabled) {
                result.testConfig = { ...result.testConfig, auth: null };
            }

            if (!runAsUser) {
                result.bttUser = null;
            } else if (defaultLogin) {
                result.bttUser = { bttUserDefaultLogin: true };
            }

            return result;
        },
        [bttStateRef, testType?.bttTest, testType?.showBttAuth]
    );

    // VoIP save transform — ensures backend-required fields are present.
    // Applies to ALL VoIP types (VoipPulse AND VoipPhone), not just agent-to-agent.
    // portal/ui computes sipUrl via $scope.$watch on callVia.head/body changes
    // (voipPulseTestConfig.directive.js:70-71, 157-159) and sets jitter default
    // in both controller inits (voipPulseTestConfig.directive.js:344,
    // voipPulseToPhoneConfig.controller.js:63).
    // In React, jitter is registered in CallSettingsFieldSection (shared by both
    // VoipPulse and VoipPhone), and sipUrl is always derived — so we compute it
    // here at save time rather than watching callVia changes.
    const isVoIP = testType?.group === TYPE_VOIP;
    const voipTransform = useCallback(
        (dataToSave) => {
            if (!isVoIP) {
                return dataToSave;
            }

            const testConfig = { ...dataToSave.testConfig };

            // Compute sipUrl from callVia (portal/ui: sipUrlChange watcher)
            // Only VoipPulse has callVia — VoipPhone uses testConfig.phone instead
            const head = get(testConfig, 'callVia.head', '');
            const body = get(testConfig, 'callVia.body', '');
            if (head || body) {
                testConfig.sipUrl = head + body;
            }

            // Ensure jitter has a value (portal/ui: DEFAULT_JITTER = 50)
            if (testConfig.jitter == null) {
                testConfig.jitter = DEFAULT_JITTER;
            }

            return { ...dataToSave, testConfig };
        },
        [isVoIP]
    );

    // WiFi save transform — matches portal/ui prepareForSave() + child agent propagation.
    // 1) Forces adapterType='wireless' for ALL WiFi types (portal/ui sets this at save time,
    //    not during UI editing — wifiConnect/wiFiMultiBssidConnection use 'default' in the
    //    agent picker so they fetch dynamicWifi nPoints instead of static wifi nPoints).
    // 2) Propagates parent agent config to selected child subtests for wifiConnectMultiTest.
    //    Fire-and-forget: child saves do not block the parent save (matching portal/ui behavior).
    const wifiSaveTransform = useCallback(
        (dataToSave) => {
            if (!WIFI_TEST_TYPES.has(testType?.name)) {
                return dataToSave;
            }

            const result = { ...dataToSave, adapterType: WIRELESS };

            if (testType?.name === 'wifiConnectMultiTest') {
                syncChildTestAgents(result, wifiTestListRef.current, matchingAgentsRef.current, (childTest) => {
                    dataService.put(`/ngp/v2/service-tests/${childTest._id}`, childTest).catch(() => {
                        // Fire-and-forget: log but don't block parent save
                    });
                });
            }

            return result;
        },
        [testType?.name, wifiTestListRef, matchingAgentsRef, dataService]
    );

    // Chains all transforms for save paths. Execution order (inside-out):
    // testConfigTransform → bttTransform → thresholdsTransform → scheduleTransform → agentsTransform → beaconTransform → voipTransform → wifiSaveTransform
    const submitTransform = useCallback(
        (dataToSave) =>
            wifiSaveTransform(
                voipTransform(
                    beaconTransform(
                        agentsTransform(
                            scheduleTransform(thresholdsTransform(bttTransform(testConfigTransform(dataToSave))))
                        )
                    )
                )
            ),
        [
            wifiSaveTransform,
            voipTransform,
            beaconTransform,
            agentsTransform,
            scheduleTransform,
            thresholdsTransform,
            bttTransform,
            testConfigTransform,
        ]
    );

    // Applied on CREATE only — merges defaults, sets type and timestamps.
    // Must NOT run on updates, or it would overwrite createdAt/updatedAt.
    const createSubmitTransform = useCallback(
        (dataToSave) => {
            return extend({}, TEST_NEW_DETAIL_OBJECT, dataToSave, {
                type: typeId,
                createdAt: getSystemTime().toISOString(),
                updatedAt: getSystemTime().toISOString(),
            });
        },
        [typeId]
    );

    return { submitTransform, createSubmitTransform };
}
